import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fieldErrorsFrom422, formatApiErrorDetail } from "@/lib/api";
import { PROJECT_STATUSES } from "@/components/project/ProjectBits";
import { FormField, adminInput } from "./shared";

const BLANK = { name: "", client_id: "", description: "", status: "Planning", progress: "0", due_date: "" };

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Name is required";
  if (!f.client_id) e.client_id = "Please select a client";
  const p = Number(f.progress);
  if (f.progress === "" || !Number.isInteger(p) || p < 0 || p > 100) e.progress = "Progress must be a whole number between 0 and 100";
  return e;
};

export const ProjectDialog = ({ open, item, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [clients, setClients] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(item ? { ...BLANK, ...item, progress: String(item.progress), due_date: item.due_date || "" } : BLANK);
    setErrors({}); setServerError("");
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, [open, item]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSave({ name: form.name.trim(), client_id: form.client_id, description: form.description, status: form.status, progress: Number(form.progress), due_date: form.due_date || null });
    } catch (err) {
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      if (Object.keys(fe).length) setErrors(fe);
      else setServerError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="project-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">{item ? "Edit project" : "New project"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="project-name" label="Project name" error={errors.name}>
            <input id="project-name" data-testid="project-input-name" value={form.name} onChange={set("name")} className={adminInput(errors.name)} />
          </FormField>
          <FormField id="project-client_id" label="Client" error={errors.client_id}>
            <select id="project-client_id" data-testid="project-input-client" value={form.client_id} onChange={set("client_id")} className={adminInput(errors.client_id)}>
              <option value="">Select a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
            </select>
          </FormField>
          <FormField id="project-description" label="Description">
            <textarea id="project-description" rows={3} data-testid="project-input-description" value={form.description} onChange={set("description")} className={adminInput(false)} />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField id="project-status" label="Status">
              <select id="project-status" data-testid="project-input-status" value={form.status} onChange={set("status")} className={adminInput(false)}>
                {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField id="project-progress" label="Progress %" error={errors.progress}>
              <input id="project-progress" type="number" min="0" max="100" data-testid="project-input-progress" value={form.progress} onChange={set("progress")} className={adminInput(errors.progress)} />
            </FormField>
            <FormField id="project-due" label="Due date" error={errors.due_date}>
              <input id="project-due" type="date" data-testid="project-input-due-date" value={form.due_date} onChange={set("due_date")} className={adminInput(errors.due_date)} />
            </FormField>
          </div>
          {serverError && <p data-testid="project-server-error" className="text-sm text-red-400">{serverError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="project-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="project-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
